import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import flowerLogo from '../../assets/gymea-flower-only.png';
import './WelcomeModal.css';

interface ReportSeasonModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Mirrors the WelcomeModal portal/escape/backdrop pattern and reuses its styles. */
export const ReportSeasonModal: React.FC<ReportSeasonModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    // Only touch the scroll lock while open, and restore what was there, so a closed
    // instance never unlocks the page behind another modal.
    if (!isOpen) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="welcome-modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="welcome-modal-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-season-modal-title"
      >
        <div className="welcome-modal-header">
          <button
            className="welcome-modal-close"
            onClick={onClose}
            type="button"
            aria-label="Close report season announcement"
            // The dialog opens on a timer, so move focus into it for keyboard and screen-reader users.
            autoFocus
          >
            ×
          </button>
        </div>

        <div className="welcome-modal-content">
          <div className="welcome-modal-visual">
            <div className="clean-flower-visual">
              <img src={flowerLogo} alt="Gymea Lily Flower" className="clean-flower-image" />
            </div>
          </div>

          <div className="welcome-modal-text">
            <h1 id="report-season-modal-title" className="welcome-modal-title">
              It's report season!
            </h1>

            <div className="welcome-modal-message">
              <p>Have you tried using Gymea Edu to lighten your load?</p>

              <p>
                We've recently given report comment generation a complete overhaul. Pop in a few
                quick notes about each student and get polished, personalised report comments back.
              </p>

              <p className="welcome-modal-signature">- The Gymea Edu Team</p>
            </div>

            <Link to="/dashboard#reports" className="welcome-modal-button" onClick={onClose}>
              Try report comments
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
